import barba from "@barba/core";
import gsap from "gsap";

import { killAllScrollTriggers, resetScrollPosition, stopScroll, startScroll, requestScrollTriggerRefresh, rebindJumpLinks, removeScrollElements, addScrollElements, checkInitialHash, disableAllScrollTriggers, enableAllScrollTriggers, killScrollTriggersFor } from "./scroll";
import { calcElHeight, calcResize, prefersReducedMotion } from "../utils/variables";
import { resetCursorState, recomputeCursorState, setCursorPageLoading } from "../components/cursor";
import { resetMenuTrigger, hideMenuTrigger, closeMenu } from "../layout/menu";
import initWebGL, { getWebGLInstance, rekeyWebGLInstance } from "../components/webgl";
import initLogo, { getLogoInstance } from "../components/logo";
import { runLayoutDefault } from "../utils/runner-functions";
import { getRootPageUrl } from "../layout/close-navigation";
import initSplitText, { killSplitText } from "./split-text";
import { applyHeroOrder } from "../utils/hero-order";

interface FrozenContainerElement extends HTMLElement {
    frozenScrollY: number;
    preserveObserver: MutationObserver | undefined;
    blurredTargets: Element[] | undefined;
    pausedVideos: HTMLVideoElement[] | undefined;
    pausedAnimations: Animation[] | undefined;
    canvasSnapshots: FrozenCanvasSnapshot[] | undefined;
}

type FrozenCanvasSnapshot = { canvas: HTMLCanvasElement; image: HTMLImageElement; display: string };

const mediaTagPattern = /<(\/?)(video|audio)\b/gi;
const videoTagPattern = /<video\b([^>]*)>([\s\S]*?)<\/video>/gi;
const titleParser = new DOMParser();

const modalNamespaces = ["work-detail", "work-detail-music", "news-detail", "clients"];
const footerHiddenNamespaces = ["contact", "clients"];
const fullHeaderFirstLoadNamespaces = ["work", "news", "contact"];
const homeUiSelector = "header[data-header], .hero__ui, .hero__description";
const homeUiRevealDelay = 0.95;

let pageInitCallback: () => Promise<void> | void;
let previousUrl: string | null = null;
let frozenContainerTop = 0;
let frozenVisibleHeight = 0;
const listingScrollPositions = new Map<string, number>();
let frozenBgContainer: HTMLElement | null = null;
let startReveal: () => void = () => {};
let warmVideo: HTMLVideoElement | null = null;
let warmHref: string | null = null;
let earlyLinkHref: string | null = null;
let earlyLinkFallback: number | null = null;

function readSession(key: string) {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

export function writeSession(key: string, value: string) {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        return;
    }
}

function holdEarlyLinkClick(event: MouseEvent) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest<HTMLAnchorElement>("a[href]");
    if (!link || link.target || link.hasAttribute("download") || link.closest("[data-barba-prevent], #wpadminbar")) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (/^\/wp-(admin|login)/.test(url.pathname)) return;
    if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) return;

    event.preventDefault();
    earlyLinkHref = url.href;
    earlyLinkFallback ??= window.setTimeout(releaseEarlyLinkClick, 8000);
}

function releaseEarlyLinkClick() {
    stopHoldingEarlyLinks();
    if (earlyLinkHref) window.location.assign(earlyLinkHref);
}

function stopHoldingEarlyLinks() {
    document.removeEventListener("click", holdEarlyLinkClick);
    window.removeEventListener("error", releaseEarlyLinkClick);
    if (earlyLinkFallback !== null) window.clearTimeout(earlyLinkFallback);
    earlyLinkFallback = null;
}

document.addEventListener("click", holdEarlyLinkClick);
window.addEventListener("error", releaseEarlyLinkClick);

export function getPreviousUrl() {
    return previousUrl;
}

export function setPageInitCallback(fn: () => Promise<void> | void) {
    pageInitCallback = fn;
}

function freezeContainer(container: HTMLElement, zIndex: number) {
    const scale = gsap.getProperty(container, "scale");
    gsap.set(container, { scale: 1 });
    frozenContainerTop = container.getBoundingClientRect().top;
    gsap.set(container, { scale });
    (container as FrozenContainerElement).frozenScrollY = window.scrollY;

    gsap.set(container, {
        position: "fixed",
        top: frozenContainerTop,
        left: 0,
        width: "100%",
        zIndex,
        pointerEvents: "none",
        // overflow: "hidden", // it messes up with sticky position
    });
}

function preserveContainer(container: HTMLElement) {
    const parent = container.parentElement;
    if (!parent) return;

    const observer = new MutationObserver(() => {
        if (!document.body.contains(container)) {
            parent.appendChild(container);
            if ((container as FrozenContainerElement).pausedAnimations) pauseContainerAnimations(container);
        }
    });
    observer.observe(parent, { childList: true });

    (container as FrozenContainerElement).preserveObserver = observer;
}

function releaseContainer(container: HTMLElement) {
    const observer = (container as FrozenContainerElement).preserveObserver as MutationObserver | undefined;
    observer?.disconnect();
    delete (container as FrozenContainerElement).preserveObserver;
}

function disposeContainer(container: HTMLElement) {
    window.dispatchEvent(new CustomEvent("barba:container-dispose", { detail: { container } }));
    killScrollTriggersFor(container);
    getWebGLInstance(container)?.dispose();
    getLogoInstance(container)?.dispose();
    killSplitText(container);
}

function releaseStaleFrozenBg() {
    if (!frozenBgContainer) return;
    releaseContainer(frozenBgContainer);
    disposeContainer(frozenBgContainer);
    frozenBgContainer.remove();
    frozenBgContainer = null;
}

function updatePageTheme(container: HTMLElement) {
    const isDark = container.dataset.pageTheme === "dark";
    container.classList.toggle("page--dark", isDark);
    document.body.setAttribute("data-theme", isDark ? "dark" : "light");
}

const navBreakMq = window.matchMedia(`(max-width: ${1232 - 0.02}px)`);

let hasSeenHeaderThisLoad = false;
let headerToggleHasRun = false;

function applyHeaderToggle(scope?: HTMLElement) {
    const root = scope ?? document.querySelector<HTMLElement>('[data-barba="container"]');
    if (!root) return;

    const minimal = root.querySelector<HTMLElement>('header[data-header="minimal"]');
    const full = root.querySelector<HTMLElement>('header[data-header="full"]');
    if (!minimal || !full) return;

    if (!headerToggleHasRun) {
        hasSeenHeaderThisLoad = readSession("hasSeenHeader") === "1";
        headerToggleHasRun = true;
    } else {
        hasSeenHeaderThisLoad = true;
    }
    writeSession("hasSeenHeader", "1");

    const namespace = root.dataset.barbaNamespace || "";
    const showFull = !navBreakMq.matches && !hasSeenHeaderThisLoad && fullHeaderFirstLoadNamespaces.includes(namespace);

    full.hidden = !showFull;
    minimal.hidden = showFull;
}

function updatePageTitle(html: string) {
    const match = html.match(/<title>([^<]*)<\/title>/i);
    if (match) document.title = match[1];
}

function closeMenuIfOpen() {
    const menu = document.querySelector<HTMLDialogElement>(".menu");
    if (menu?.open) {
        void closeMenu({ keepTriggerHidden: true });
    } else {
        hideMenuTrigger();
    }
}

function updateFooterVisibility(namespace: string) {
    const hideFooter = footerHiddenNamespaces.includes(namespace);
    const footer = document.querySelector<HTMLElement>(".footer");
    if (footer) footer.hidden = hideFooter;
}

const menuPageIdByNamespace: Record<string, string> = {
    "work-detail": "work",
    "work-detail-music": "work",
    "news-detail": "news",
};

function updateMenuActiveState(namespace: string) {
    const pageId = menuPageIdByNamespace[namespace] ?? namespace;

    document.querySelectorAll<HTMLAnchorElement>(".menu [data-page-id]").forEach(link => {
        link.classList.toggle("active", link.dataset.pageId === pageId);
    });
}

function blurVisibleContent(container: HTMLElement) {
    const canvasEl = container.querySelector<HTMLCanvasElement>(".webgl canvas");
    const viewportBottom = window.innerHeight;

    const visibleNonWebglChildren = Array.from(container.children).filter(child => {
        if (child.classList.contains("webgl") || child.querySelector(".webgl")) return false;
        const rect = child.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < viewportBottom;
    });

    const targets = canvasEl ? [canvasEl, ...visibleNonWebglChildren] : visibleNonWebglChildren;
    gsap.set(targets, { filter: "blur(4px)" });

    (container as FrozenContainerElement).blurredTargets = targets;
}

function clearBlur(container: HTMLElement) {
    const targets = (container as FrozenContainerElement).blurredTargets as Element[] | undefined;
    if (!targets || targets.length === 0) return;

    gsap.set(targets, {
        filter: "blur(0px)",
        onComplete: () => {
            gsap.set(targets, { clearProps: "filter" });
        },
    });

    delete (container as FrozenContainerElement).blurredTargets;
}

export function pauseContainerPlayback(container: HTMLElement) {
    const playingVideos = Array.from(container.querySelectorAll<HTMLVideoElement>("video")).filter(video => !video.paused);
    playingVideos.forEach(video => video.pause());

    (container as FrozenContainerElement).pausedVideos = playingVideos;
    pauseContainerAnimations(container);
}

function pauseContainerAnimations(container: HTMLElement) {
    const runningAnimations = container.getAnimations({ subtree: true }).filter(animation => animation.playState === "running");
    runningAnimations.forEach(animation => animation.pause());

    (container as FrozenContainerElement).pausedAnimations = runningAnimations;
}

export function resumeContainerPlayback(container: HTMLElement) {
    const { pausedVideos, pausedAnimations } = container as FrozenContainerElement;

    pausedVideos?.forEach(video => void video.play().catch(() => {}));
    pausedAnimations?.filter(animation => animation.playState === "paused").forEach(animation => animation.play());

    delete (container as FrozenContainerElement).pausedVideos;
    delete (container as FrozenContainerElement).pausedAnimations;
}

function flattenFrozenLayers(container: HTMLElement) {
    const webglCanvas = container.querySelector<HTMLCanvasElement>(".webgl canvas");
    const logoCanvas = container.querySelector<HTMLCanvasElement>(".logo canvas");
    const canvasSnapshots: FrozenCanvasSnapshot[] = [];

    (container as FrozenContainerElement).canvasSnapshots = canvasSnapshots;

    if (webglCanvas) void swapCanvasForSnapshot(container, webglCanvas, getWebGLInstance(container)?.snapshot(), canvasSnapshots);
    if (logoCanvas) void swapCanvasForSnapshot(container, logoCanvas, getLogoInstance(container)?.snapshot(), canvasSnapshots);
}

async function swapCanvasForSnapshot(container: HTMLElement, canvas: HTMLCanvasElement, snapshot: Promise<Blob | null> | undefined, canvasSnapshots: FrozenCanvasSnapshot[]) {
    const blob = await snapshot;
    if (!blob) return;

    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.src = url;
    const isDecoded = await image.decode().then(
        () => true,
        () => false
    );
    URL.revokeObjectURL(url);
    if (!isDecoded || (container as FrozenContainerElement).canvasSnapshots !== canvasSnapshots) return;

    const { position, left, top } = getComputedStyle(canvas);
    image.style.cssText = canvas.style.cssText;
    Object.assign(image.style, { position, left, top });
    canvas.after(image);

    canvasSnapshots.push({ canvas, image, display: canvas.style.display });
    canvas.style.display = "none";
}

function restoreFrozenLayers(container: HTMLElement) {
    const { canvasSnapshots } = container as FrozenContainerElement;

    canvasSnapshots?.forEach(({ canvas, image, display }) => {
        canvas.style.display = display;
        image.remove();
    });

    delete (container as FrozenContainerElement).canvasSnapshots;
}

function applyPageState(container: HTMLElement, html: string) {
    const namespace = container.dataset.barbaNamespace || "";

    updatePageTheme(container);
    updateMenuActiveState(namespace);
    updatePageTitle(html);
    applyHeaderToggle(container);

    document.body.classList.toggle("is-home", namespace === "home");

    if (getPreviousUrl() !== null) {
        container.querySelectorAll<HTMLElement>(".video_overlay").forEach(el => {
            el.setAttribute("data-cursor-text", "Pause");
        });
    }
}

async function runPageInit() {
    resetCursorState();
    startScroll();
    rebindJumpLinks();
    resetMenuTrigger();
    await pageInitCallback?.();
    requestScrollTriggerRefresh(100);
    checkInitialHash();
    recomputeCursorState();
}

function isInFirstScreen(el: Element) {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
}

function waitForVideoFrame(video: HTMLVideoElement) {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve();

    return new Promise<void>(resolve => {
        video.addEventListener("loadeddata", () => resolve(), { once: true });
        video.addEventListener("error", () => resolve(), { once: true });
    });
}

function waitForFirstScreenMedia(container: HTMLElement) {
    const videos = Array.from(container.querySelectorAll<HTMLVideoElement>("video:not([preload='none'])")).filter(isInFirstScreen);
    const images = Array.from(container.querySelectorAll<HTMLImageElement>("img:not([hidden])")).filter(isInFirstScreen);
    images.forEach(image => {
        image.loading = "eager";
    });
    const heroReady = getWebGLInstance(container)?.heroSlider.ready ?? Promise.resolve();

    const mediaReady = Promise.all([heroReady, ...videos.map(waitForVideoFrame), ...images.map(image => image.decode().catch(() => {}))]);
    const mediaTimeout = new Promise<void>(resolve => window.setTimeout(resolve, 1500));

    return Promise.race([mediaReady, mediaTimeout]);
}

function hideHomeUi(container: HTMLElement) {
    gsap.set(container.querySelectorAll(homeUiSelector), { autoAlpha: 0, transition: "none" });
}

function revealHomeUi(container: HTMLElement) {
    gsap.fromTo(container.querySelectorAll(homeUiSelector), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.7, delay: homeUiRevealDelay, ease: "power2.out", clearProps: "opacity,visibility,transition" });
}

function parseFetchedPageWithoutMedia(html: string) {
    return titleParser.parseFromString(html.replace(mediaTagPattern, "<$1x-$2"), "text/html");
}

function findLeadVideo(html: string) {
    videoTagPattern.lastIndex = 0;
    for (let match = videoTagPattern.exec(html); match; match = videoTagPattern.exec(html)) {
        const [, attrs, inner] = match;
        if (/preload=["']none/.test(attrs)) continue;

        const src = attrs.match(/\ssrc=["']([^"']+)/)?.[1] ?? inner.match(/<source\b[^>]*\ssrc=["']([^"']+)/)?.[1];
        if (src) return { src: src.replace(/&amp;/g, "&"), isCrossOrigin: /\scrossorigin\b/.test(attrs) };
    }
    return null;
}

function releaseWarmVideo() {
    if (!warmVideo) return;
    warmVideo.removeAttribute("src");
    warmVideo.load();
    warmVideo = null;
    warmHref = null;
}

async function warmNextPageVideo(href: string) {
    if (href === warmHref) return;
    releaseWarmVideo();
    warmHref = href;

    barba.prefetch(href);
    const response = await barba.cache.get(href)?.request?.catch(() => undefined);
    const leadVideo = response ? findLeadVideo(response.html) : null;
    if (!leadVideo || warmHref !== href) return;

    const video = document.createElement("video");
    video.disableRemotePlayback = true;
    video.muted = true;
    video.preload = "auto";
    if (leadVideo.isCrossOrigin) video.crossOrigin = "anonymous";
    video.src = new URL(leadVideo.src, href).href;
    warmVideo = video;
}

function handleLinkIntent(e: Event) {
    const link = (e.target as Element).closest<HTMLAnchorElement>("a[href]");
    if (!link || link.origin !== window.location.origin || link.target === "_blank" || link.href === window.location.href) return;

    void warmNextPageVideo(barba.url.getAbsoluteHref(link.href));
}

function startClickCue() {
    if (document.documentElement.hasAttribute("data-navigating")) return;

    const containers = document.querySelectorAll<HTMLElement>('[data-barba="container"]');
    const container = containers[containers.length - 1];
    const rect = container.getBoundingClientRect();

    gsap.to(container, {
        scale: 0.985,
        transformOrigin: `50% ${window.innerHeight / 2 - rect.top}px`,
        duration: 0.6,
        ease: "power3.out",
    });
}

export function initBarba() {
    ["mouseover", "touchstart", "focusin"].forEach(type => {
        document.addEventListener(type, handleLinkIntent, { passive: true });
    });
    window.addEventListener("barba:nav-end", releaseWarmVideo);

    navBreakMq.addEventListener("change", () => applyHeaderToggle());
    barba.dom.toDocument = parseFetchedPageWithoutMedia;

    let transitionTl: gsap.core.Timeline | null = null;
    let finishTransition: (() => void) | null = null;
    let isPageRequested = false;
    let queuedNavigation: Parameters<typeof barba.go> | null = null;
    const startNavigation = barba.go.bind(barba);
    barba.go = (...args: Parameters<typeof barba.go>) => {
        if (isPageRequested) {
            const [, trigger, event] = args;
            if (trigger === "popstate" || typeof trigger === "string") queuedNavigation = args;
            if (trigger !== "popstate") event?.preventDefault();
            return Promise.resolve();
        }

        return startNavigation(...args);
    };
    const runQueuedNavigation = () => {
        isPageRequested = false;
        if (!queuedNavigation) return;

        const navigation = queuedNavigation;
        queuedNavigation = null;
        const [href, trigger] = navigation;
        if (trigger === "popstate" && href !== window.location.href) return;
        void barba.go(...navigation);
    };
    const startPageRequest = barba.page.bind(barba);
    barba.page = (...args: Parameters<typeof barba.page>) => {
        isPageRequested = true;
        setCursorPageLoading(true);
        startClickCue();
        const pageRequest = startPageRequest(...args);
        void pageRequest.then(runQueuedNavigation, runQueuedNavigation);
        return pageRequest;
    };
    barba.init({
        preventRunning: true,
        timeout: 10000,

        transitions: [
            {
                name: "default",
                sync: true,
                async beforeLeave({ current, next }) {
                    window.dispatchEvent(new Event("barba:nav-start"));
                    document.documentElement.setAttribute("data-navigating", "");

                    const isPageToModalTransition = next.namespace && modalNamespaces.includes(next.namespace);
                    const isModalToPageTransition = current.namespace && modalNamespaces.includes(current.namespace);
                    const isReturningToOrigin = getRootPageUrl() === next.url.href && frozenBgContainer !== null;

                    previousUrl = current.url.href;
                    const nextNamespace = next.container.dataset.barbaNamespace || "";
                    if (nextNamespace === "clients") void runLayoutDefault("clients");

                    closeMenuIfOpen();
                    resetCursorState();
                    removeScrollElements(current.container);
                    stopScroll();
                    freezeContainer(current.container, 5);

                    if (isPageToModalTransition && !isModalToPageTransition) {
                        const currentNs = current.container.dataset.barbaNamespace || "";

                        listingScrollPositions.set(currentNs, window.scrollY);

                        preserveContainer(current.container as HTMLElement);

                        frozenBgContainer = current.container as HTMLElement;
                    }

                    const footer = document.querySelector<HTMLElement>(".footer");
                    if (footer) footer.hidden = true;

                    if (isModalToPageTransition && isReturningToOrigin) {
                        getLogoInstance(frozenBgContainer!)?.setFrozen(false);

                        const visibleHeight = window.innerHeight - frozenContainerTop;
                        frozenVisibleHeight = visibleHeight;
                        gsap.set(current.container, { height: visibleHeight, overflow: "hidden" });
                    } else {
                        if (isModalToPageTransition && !isPageToModalTransition) {
                            releaseStaleFrozenBg();
                        }
                        gsap.set(next.container, { height: "100vh", overflow: "hidden" });
                    }
                },
                async leave({ current, next }) {
                    finishTransition?.();
                    transitionTl?.kill();

                    const isPageToModalTransition = next.namespace && modalNamespaces.includes(next.namespace);
                    const isModalToPageTransition = current.namespace && modalNamespaces.includes(current.namespace);
                    const isModalToModalTransition = isModalToPageTransition && isPageToModalTransition;
                    const isReturningToOrigin = getRootPageUrl() === next.url.href && frozenBgContainer !== null;
                    const isRevealingNext = !(isModalToPageTransition && isReturningToOrigin);
                    const isHomeReveal = next.namespace === "home" && !isReturningToOrigin;

                    const leaveEl = current.container as HTMLElement;
                    const enterEl = next.container as HTMLElement;

                    if (isModalToPageTransition && isReturningToOrigin) {
                        // nothing
                    } else if (isModalToModalTransition) {
                        gsap.set(leaveEl, { zIndex: 5 });
                        gsap.set(enterEl, { zIndex: 6 });
                    } else {
                        gsap.set(leaveEl, { zIndex: 1 });
                        gsap.set(enterEl, { zIndex: 5 });
                    }

                    if (!isPageToModalTransition) {
                        if (isModalToPageTransition) {
                            killScrollTriggersFor(current.container);
                        } else {
                            killAllScrollTriggers();
                        }
                    } else if (isModalToModalTransition) {
                        killAllScrollTriggers();
                    } else {
                        disableAllScrollTriggers();
                    }

                    if (isRevealingNext) {
                        gsap.set(enterEl, { clipPath: "inset(50% round 3%)" });
                        gsap.to(enterEl, { clipPath: "inset(47% round 3%)", duration: 0.4, ease: "power3.out" });
                        await waitForFirstScreenMedia(enterEl);
                    }

                    return new Promise<void>(resolve => {
                        setCursorPageLoading(false);
                        finishTransition = resolve;
                        transitionTl = gsap.timeline({ onComplete: resolve });

                        startReveal();
                        if (isHomeReveal) revealHomeUi(enterEl);

                        if (prefersReducedMotion) {
                            if (isModalToPageTransition && isReturningToOrigin) {
                                transitionTl.to(leaveEl, { autoAlpha: 0, duration: 0.3, ease: "power1.out" });
                            } else {
                                transitionTl.set(enterEl, { clipPath: "inset(0% round 0%)" });
                                transitionTl.fromTo(enterEl, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: "power1.out" });
                            }
                        } else if (isModalToPageTransition && isReturningToOrigin) {
                            const topOpen = Math.max(0, -frozenContainerTop);
                            const clipCenter = window.innerHeight / 2 - frozenContainerTop;
                            const bottomCollapsed = frozenVisibleHeight - clipCenter;

                            transitionTl.fromTo(
                                leaveEl,
                                { clipPath: `inset(${topOpen}px 0% 0px 0% round 0%)` },
                                {
                                    clipPath: `inset(${clipCenter}px 50% ${bottomCollapsed}px 50% round 3%)`,
                                    duration: 0.8,
                                    ease: "power3.inOut",
                                }
                            );
                        } else {
                            if (!isPageToModalTransition) {
                                const leaveInstance = getWebGLInstance(leaveEl);
                                leaveInstance?.planePool.playLeavePop();
                                leaveInstance?.heroSlider.playLeavePop();
                            }

                            transitionTl.to(enterEl, {
                                clipPath: "inset(0% round 2%)",
                                duration: 0.8,
                                ease: "power3.inOut",
                                overwrite: "auto",
                            });
                            transitionTl.to(
                                enterEl,
                                {
                                    clipPath: "inset(0% round 0%)",
                                    duration: 0.45,
                                    ease: "power2.out",
                                },
                                "-=0.15"
                            );
                        }

                        if (isPageToModalTransition && !isModalToPageTransition) {
                            transitionTl.to(
                                leaveEl,
                                {
                                    scale: 1,
                                    duration: 0.8,
                                    ease: "power3.inOut",
                                    clearProps: "transform,transformOrigin",
                                },
                                0
                            );
                        }

                        if (isModalToPageTransition && !isPageToModalTransition) {
                            document.body.classList.remove("has-modal-backdrop");
                        }
                    });
                },

                async beforeEnter({ current, next }) {
                    const isModalToPageTransition = current.namespace && modalNamespaces.includes(current.namespace);
                    const isReturningToOrigin = getRootPageUrl() === next.url.href && frozenBgContainer !== null;

                    const nextIsModal = !!next.namespace && modalNamespaces.includes(next.namespace);

                    if (nextIsModal) {
                        document.body.classList.add("has-modal-backdrop");
                        next.container.classList.add("barba-modal");
                    }

                    if (isModalToPageTransition && isReturningToOrigin) {
                        const restoreY = (frozenBgContainer as FrozenContainerElement)?.frozenScrollY ?? 0;

                        window.scrollTo(0, restoreY);
                        resetScrollPosition(restoreY);

                        gsap.set(next.container, { autoAlpha: 0 });
                        clearBlur(frozenBgContainer!);
                        restoreFrozenLayers(frozenBgContainer!);
                        resumeContainerPlayback(frozenBgContainer!);

                        return;
                    }

                    applyPageState(next.container, next.html);
                    applyHeroOrder(next.container);
                    calcResize(false, next.container);
                    const isHome = next.namespace === "home";
                    const revealGate = new Promise<void>(resolve => {
                        startReveal = resolve;
                    });
                    if (isHome) hideHomeUi(next.container);
                    initSplitText(next.container, isHome ? homeUiRevealDelay : 0.35, revealGate);
                    await initWebGL(next.container, 0.3, revealGate);
                    await initLogo(next.container);
                },

                async afterEnter({ current, next }) {
                    const isPageToModalTransition = next.namespace && modalNamespaces.includes(next.namespace);
                    const isModalToPageTransition = current.namespace && modalNamespaces.includes(current.namespace);
                    const isReturningToOrigin = getRootPageUrl() === next.url.href && frozenBgContainer !== null;

                    gsap.set(next.container, { height: "auto", overflow: "", clearProps: "clipPath,opacity,visibility" });

                    if (isModalToPageTransition && !isPageToModalTransition) {
                        if (isReturningToOrigin && frozenBgContainer) {
                            const bgCont = frozenBgContainer;
                            const fgCont = next.container as HTMLElement;

                            releaseContainer(bgCont);
                            gsap.set(bgCont, { clearProps: "position,top,left,width,height,zIndex,pointerEvents" });
                            bgCont.removeAttribute("aria-hidden");
                            bgCont.removeAttribute("inert");

                            fgCont.innerHTML = ""; // discard barba's redundant fresh fetch — bgCont's content replaces it entirely
                            while (bgCont.firstChild) fgCont.appendChild(bgCont.firstChild);
                            fgCont.className = bgCont.className;

                            const instance = getWebGLInstance(bgCont);
                            if (instance) {
                                instance.planePool.rebindRoot(fgCont);
                                rekeyWebGLInstance(bgCont, fgCont);
                            }

                            getLogoInstance(bgCont)?.rebindRoot(fgCont);

                            bgCont.remove();
                            gsap.set(fgCont, { autoAlpha: 1 });
                            applyPageState(fgCont, next.html);

                            frozenBgContainer = null;
                            enableAllScrollTriggers();
                        }
                    }

                    if (isPageToModalTransition && !isModalToPageTransition) {
                        pauseContainerPlayback(current.container);
                        blurVisibleContent(current.container);
                        getLogoInstance(current.container)?.setFrozen(true);
                        flattenFrozenLayers(current.container);
                    } else {
                        disposeContainer(current.container);
                    }

                    updateFooterVisibility(next.container.dataset.barbaNamespace || "");

                    calcElHeight(document.querySelector<HTMLElement>(".footer"), "--footer-height");

                    addScrollElements(next.container);
                    await runPageInit();

                    document.documentElement.removeAttribute("data-navigating");
                    window.dispatchEvent(new Event("barba:nav-end"));
                },
            },
        ],
    });

    // Apply state for the initially loaded page.
    const initialContainer = document.querySelector<HTMLElement>('[data-barba="container"]');

    if (initialContainer) {
        applyPageState(initialContainer, document.documentElement.outerHTML);
    }

    stopHoldingEarlyLinks();
    if (earlyLinkHref) {
        const href = earlyLinkHref;
        earlyLinkHref = null;
        if (href.split("#")[0] !== window.location.href.split("#")[0]) void barba.go(href);
    }
}
